<template>
  <div
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    ref="tooltip-source"
    class="vue-tooltip"
  >
    <slot name="text"></slot>

    <Teleport to="#tooltip-anchor">
      <transition :name="`tooltip-${placement}`">
        <div
          v-if="isShowTooltip && hasTooltip"
          class="vue-tooltip--component"
          :class="[`vue-tooltip__${placement}`]"
          :style="calculatedStyle"
          @mouseenter="onTooltipHover = true"
          @mouseleave="onTooltipHover = false"
        >
          <div class="vue-tooltip__body">
            <slot name="tooltip"></slot>
          </div>
          <div class="vue-tooltip__arrow" data-pc-section="arrow"></div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, useTemplateRef, useSlots } from 'vue';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

const props = withDefaults(
  defineProps<{
    position?: TooltipPosition;
    hasTooltip?: boolean;
  }>(),
  {
    position: 'bottom',
    hasTooltip: true,
  },
);

const tooltipSource = useTemplateRef('tooltip-source');

const isShowTooltip = ref(false);
const onTooltipHover = ref(false);
const calculatedStyle = ref({});
const slots = useSlots();
const placement = ref('bottom');

const onMouseEnter = () => {
  calculateSizePosition();
  nextTick(() => {
    isShowTooltip.value = true;
  });

  document.addEventListener('scroll', onScroll);
};

const onScroll = () => {
  isShowTooltip.value = false;
  document.removeEventListener('scroll', onScroll);
};

const onMouseLeave = () => {
  setTimeout(() => {
    if (onTooltipHover.value) {
      isShowTooltip.value = true;
      return;
    }
    isShowTooltip.value = false;
    document.removeEventListener('scroll', onScroll);
  }, 150);
};

const canvas = document.createElement('canvas');

const getTextSize = () => {
  const lineHeight = 20;
  const maxWidth = 400;
  const padding = 8;

  let width = 0;
  let height = lineHeight + padding * 2;

  if (slots && slots['tooltip']) {
    const tooltipSlot = slots['tooltip']();
    const tooltipText = tooltipSlot[0].children;

    const context = canvas.getContext('2d');
    if (context) {
      context.font = '16px Arial';
      const measurement = context.measureText(`${tooltipText}`);

      if (measurement.width <= maxWidth) {
        width = measurement.width + padding * 2;
      } else {
        width = maxWidth + padding * 2;
        height = Math.ceil(measurement.width / maxWidth) * lineHeight + padding * 2;
      }
    }
  }

  return {
    height,
    width,
  };
};

const getAvailablePlacements = () => {
  const sourcePos = tooltipSource.value?.getBoundingClientRect();
  const tooltipSize = getTextSize();

  const placements = [];

  if (sourcePos) {
    const widthFits = sourcePos.left > tooltipSize.width / 2;

    if (window.innerHeight - sourcePos.bottom > tooltipSize.height && widthFits) {
      placements.push('bottom');
    }
    if (sourcePos.left > tooltipSize.width + 8) {
      placements.push('left');
    }
    if (window.innerHeight - sourcePos.top > tooltipSize.height && widthFits) {
      placements.push('top');
    }
    if (window.innerWidth - sourcePos.right > tooltipSize.width + 8) {
      placements.push('right');
    }
  }

  return placements;
};

const calculateSizePosition = () => {
  // Measure the text so we can size the tooltip
  const tooltipSize = getTextSize();
  const sourcePos = tooltipSource.value?.getBoundingClientRect();

  if (sourcePos) {
    // Set the tooltip sizings
    let toolTop = `${sourcePos.top + sourcePos.height / 2 - tooltipSize.height / 2}px`;
    let toolLeft = `${(sourcePos.left + sourcePos.width / 2).toFixed(2)}px`;
    const toolHeight = `${tooltipSize.height}px`;
    const toolWidth = `${tooltipSize.width}px`;

    const tooltipPlacements = getAvailablePlacements();
    if (tooltipPlacements.includes(props.position)) {
      placement.value = props.position;
    } else {
      placement.value = tooltipPlacements[0];
    }

    if (placement.value === 'top') {
      toolTop = `${sourcePos.top - tooltipSize.height - 8}px`;
    } else if (placement.value === 'bottom') {
      toolTop = `${sourcePos.top + sourcePos.height + 8}px`;
    } else if (placement.value === 'left') {
      toolLeft = `${sourcePos.left - tooltipSize.width / 2 - 12}px`;
    } else if (placement.value === 'right') {
      toolLeft = `${sourcePos.right + tooltipSize.width / 2 + 12}px`;
    }

    calculatedStyle.value = {
      height: toolHeight,
      left: toolLeft,
      top: toolTop,
      width: toolWidth,
    };
  }
};
</script>

<style lang="scss">
.tooltip-left-enter-active,
.tooltip-left-leave-active,
.tooltip-right-enter-active,
.tooltip-right-leave-active,
.tooltip-top-enter-active,
.tooltip-top-leave-active,
.tooltip-bottom-enter-active,
.tooltip-bottom-leave-active {
  transition: 0.4s;
}

.tooltip-left-enter-from,
.tooltip-left-leave-to,
.tooltip-right-enter-from,
.tooltip-right-leave-to,
.tooltip-top-enter-from,
.tooltip-top-leave-to,
.tooltip-bottom-enter-from,
.tooltip-bottom-leave-to {
  opacity: 0;
}

.vue-tooltip {
  @include mixins.flex-center();
}

.vue-tooltip--component {
  @include sizing.padding('xxs');
  background: variables.$vantage-base-primitives-color-tertiary-500;
  border-radius: variables.$vantage-base-sizing-xxxs;
  border: none;
  color: variables.$vantage-base-primitives-color-background-100;
  height: max-content;
  outline: none;
  position: absolute;
  transform: translate(-50%, 0);
  z-index: 5000;
  text-align: center;

  .vue-tooltip__body {
    @include typography.font-body($size: 'sm', $weight: normal);
  }
}

.vue-tooltip__arrow {
  border-color: transparent;
  border-style: solid;
  border-width: variables.$vantage-base-sizing-xxs;
  height: 0;
  position: absolute;
  width: 0;
}

.vue-tooltip__left .vue-tooltip__arrow {
  border-left-color: variables.$vantage-base-primitives-color-tertiary-500;
  border-right-width: 0;
  margin-top: -(variables.$vantage-base-sizing-xxs);
  right: -(variables.$vantage-base-sizing-xxs);
  top: 50%;
}

.vue-tooltip__right .vue-tooltip__arrow {
  border-left-width: 0;
  border-right-color: variables.$vantage-base-primitives-color-tertiary-500;
  left: -(variables.$vantage-base-sizing-xxs);
  margin-top: -(variables.$vantage-base-sizing-xxs);
  top: 50%;
}

.vue-tooltip__bottom .vue-tooltip__arrow {
  border-bottom-color: variables.$vantage-base-primitives-color-tertiary-500;
  border-top-width: 0;
  margin-right: -(variables.$vantage-base-sizing-xxs);
  right: 50%;
  top: -(variables.$vantage-base-sizing-xxs);
}

.vue-tooltip__top .vue-tooltip__arrow {
  border-bottom-width: 0;
  border-top-color: variables.$vantage-base-primitives-color-tertiary-500;
  bottom: -(variables.$vantage-base-sizing-xxs);
  margin-right: -(variables.$vantage-base-sizing-xxs);
  right: 50%;
}

.p-menu-item .vue-tooltip,
.p-menu-item-content .vue-tooltip {
  width: 100%;
}
</style>
